// React component for the connect modal
import { Box, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { TwButton, TwButtonType } from '../../../../components/TwButtons/TwButtons';
import TwDialog from '../../../../components/TwDialog/TwDialog';
import TwField, { TwInputTypes } from '../../../../components/TwFields/TwFields';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import SalesTaxService from '../../../../providers/Settings/SalesTax.service';

interface SalesTaxCreateUpdateModalProps {
  open: boolean;
  onClose: (doCreate: boolean) => void;
  divisionId: string;
}

const SalesTaxCreateUpdateModal: React.FC<SalesTaxCreateUpdateModalProps> = ({
  open,
  onClose,
  divisionId
}) => {
  const { addNotification } = useNotifications();

  const [name, setName] = useState<string | undefined>(undefined);
  const [taxId, setTaxId] = useState<string | undefined>(undefined);
  const [rate, setRate] = useState<number | undefined>(undefined);
  const [rateString, setRateString] = useState<string | undefined>(undefined);
  const [errors, setErrors] = useState<Map<string, string>>(new Map());

  const fieldErrorMap = new Map<string, string>([
    ['name', 'Name is required'],
    ['taxId', 'Tax ID is required'],
    ['rate', 'Rate is required']
  ]);

  const handleSubmit = async (): Promise<void> => {
    // validation
    [
      [name, 'name'],
      [rate, 'rate']
    ].forEach(([field, fieldName]) => {
      if (field === undefined) {
        setErrors(errors.set(fieldName as string, fieldErrorMap.get(fieldName as string) || ''));
        addNotification({
          message: fieldErrorMap.get(fieldName as string) || '',
          variant: 'error',
          timeToShow: 2000
        });
        return;
      }
    });

    if (rate && rate > 100) {
      setErrors(errors.set('rate', 'Rate must be less than 100%'));
      addNotification({
        message: 'Rate must be less than 100% ',
        variant: 'error',
        timeToShow: 2000
      });
      return;
    }

    if (errors.size > 0) {
      return;
    }

    SalesTaxService.createSalesTax({ division_id: divisionId, name, tax_id: taxId, rate })
      .then((res) => {
        addNotification({
          message: 'Sales tax created successfully',
          variant: 'success',
          timeToShow: 3000
        });
        onClose(true);

        setName(undefined);
        setTaxId(undefined);
        setRate(undefined);
        setRateString(undefined);
      })
      .catch((err) => {
        console.error('err', err);
        addNotification({
          message: 'Error creating sales tax',
          variant: 'error',
          timeToShow: 3000
        });
      });
  };

  const handleRateChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    errors.delete('rate');
    setErrors(errors);

    let value = e.target.value;
    const regex = /^-?[0-9]*\.?[0-9]{0,2}$/;

    if (value.startsWith('.')) {
      value = `0${value}`;
    }

    if (regex.test(value)) {
      setRate(Number(value));
      setRateString(value);
    }

    if (value === '') {
      setRate(undefined);
      setRateString('');

      return;
    }
  };

  const handleTaxIdChange = (e: any): void => {
    errors.delete('taxId');
    setErrors(errors);
    setTaxId(e.target.value);
  };

  const handleNameChange = (e: any): void => {
    errors.delete('name');
    setErrors(errors);
    setName(e.target.value);
  };

  return (
    <TwDialog
      open={open}
      title={`Add tax`}
      size="sm"
      onClickClose={(): void => onClose(false)}
      actions={[
        <TwButton
          key="1"
          type={TwButtonType.secondary}
          label="Cancel"
          action={(): void => onClose(false)}
        />,
        <TwButton
          key="2"
          type={TwButtonType.primary}
          label="Add"
          disabled={errors.size > 0}
          action={handleSubmit}
        />
      ]}
    >
      <TwField
        id="sales-tax-name"
        name="sales-tax-name"
        value={name}
        label="Tax name *"
        inputType={TwInputTypes.text}
        onInputChange={(e): any => handleNameChange(e)}
        error={!!errors.get('name')}
      />

      <Stack direction="row" spacing={2}>
        <Box sx={{ width: '100%' }}>
          <TwField
            id="sales-tax-tax-id"
            name="sales-tax-tax-id"
            value={taxId}
            label="Tax item ID"
            inputType={TwInputTypes.text}
            onInputChange={(e): any => handleTaxIdChange(e)}
            error={!!errors.get('taxId')}
          />
        </Box>

        <Box sx={{ width: '100%' }}>
          <TwField
            id="sales-tax-rate"
            name="sales-tax-rate"
            value={rateString}
            label="Rate *"
            inputType={TwInputTypes.number}
            onInputChange={(e): any => handleRateChange(e)}
            error={!!errors.get('rate')}
            endAdorment={
              <Typography
                variant="body2"
                fontWeight="400"
                fontSize="14px"
                color="#9CA3AF"
                marginRight="8px"
              >
                %
              </Typography>
            }
          />
        </Box>
      </Stack>
    </TwDialog>
  );
};

export default SalesTaxCreateUpdateModal;
